import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { useStore } from "utils/useStore";
import { Grid } from "@mui/material";
import ConvoQuestionAnswerList, {
    buildConvoQuestionVisibilityMap,
    ConvoQuestion,
} from "./questionComponents/ConvoQuestionAnswerList";
import ConversationLabelingStore, {
    loadLicensedModule,
} from "./store/ConversationLabelingStore";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import ConversationsStore, {
    SidePanelType,
} from "components/Conversations/Stores/ConversationsStore";
import AcxButton from "components/UI/AcxButton";
import { LayoutDrawerStore } from "stores/Layout/LayoutDrawerStore";

export const endDialogTitle = "Confirm End Workflow";

const ConversationLabelingContainer = observer(() => {
    const store = useStore(ConversationLabelingStore);
    const conversationsStore = useStore(ConversationsStore);
    const drawerStore = useStore(LayoutDrawerStore);

    const moduleQuestions = (store.selectedLicensedModule?.sortedQuestion ?? []).map(
        (q) => {
            var convoQuestion = q as ConvoQuestion;
            convoQuestion.conversationModuleId =
                store.selectedConversationModuleId as string;
            return convoQuestion;
        },
    );
    const visibilityMap = buildConvoQuestionVisibilityMap(
        moduleQuestions,
        store.answers,
    );
    const sortedQuestions = moduleQuestions.filter(
        (question) => visibilityMap.get(question.id) ?? false,
    );

    let isMissingRequiredAnswer =
        store.isMissingRequiredAnswer(sortedQuestions);

    // Need to do this in case the user has updated soundClip name or notes during data labelling
    const syncSoundClipNameAndNoteWithStore = () => {
        const soundClipAnswers = store.answers.flatMap(
            (a) => a.soundClipAnswers,
        );
        soundClipAnswers.forEach((sca) => {
            const scFromStore = conversationsStore.activeSoundClips?.find(
                (sc) => sc.id === sca.soundClip?.id,
            );
            if (sca.soundClip && scFromStore) {
                if (sca.soundClip.segmentName !== scFromStore.segmentName) {
                    sca.soundClip.segmentName = scFromStore.segmentName;
                }
                if (sca.soundClip.note !== scFromStore.note) {
                    sca.soundClip.note = scFromStore.note;
                }
            }
        });
    };

    // If a user has de-selected an answer, need to clear notes and clips before saving
    const removeNotesAndClipsFromUnanswered = () => {
        const unAnswered = store.answers.filter((a) => a.isAnswered === false);
        unAnswered.forEach((a) => {
            a.note = "";
            a.soundClipAnswers = [];
        });
    };

    const dataLabelingActionButtons = (
        <Grid item container xs={12} justifyContent={"flex-end"} spacing={1}>
            <Grid item>
                <AcxButton
                    onClick={() => {
                        store.ReleaseDataLabelingLock();
                        conversationsStore.setSidePanel(SidePanelType.Hidden);
                    }}
                    color={"error"}
                >
                    Cancel
                </AcxButton>
            </Grid>
            <Grid item>
                <AcxButton
                    onClick={() => {
                        syncSoundClipNameAndNoteWithStore();
                        removeNotesAndClipsFromUnanswered();
                        if (
                            !!store.convoModuleInfo.find(
                                (cmi) =>
                                    cmi.conversationModuleId ===
                                    store.selectedConversationModuleId,
                            )
                        ) {
                            store.updateConversationModule();
                        } else {
                            store.createConversationModule();
                        }
                        conversationsStore.setSidePanel(SidePanelType.Hidden);
                    }}
                    disabled={isMissingRequiredAnswer}
                >
                    Save
                </AcxButton>
            </Grid>
        </Grid>
    );

    useEffect(() => {
        drawerStore.setFooterButtons(dataLabelingActionButtons);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataLabelingActionButtons, drawerStore.isOpen]);

    return (
        <Grid item minHeight={"83vh"}>
            {store.getTaskLoadingV2(loadLicensedModule) ? (
                <AcxLoadingIndicator size={75} alternate="PuffLoader" />
            ) : (
                <ConvoQuestionAnswerList
                    convoModuleId={store.selectedConversationModuleId}
                    isEditable
                    showClipUi
                    uiModel={store.uiModel}
                    sortedQuestions={sortedQuestions as ConvoQuestion[]}
                    isQuestionAutofilled={(questionId) =>
                        store.isQuestionAutofilled(questionId)
                    }
                    getAnswerForQuestion={store.getAnswerForQuestion}
                    setAnswerForQuestion={store.setAnswerForQuestion}
                    setFillInAnswerForQuestion={
                        store.setFillInAnswerForQuestion
                    }
                />
            )}
        </Grid>
    );
});

export default ConversationLabelingContainer;
